import React, {
  ReactNode,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import {
  Input,
  View,
  Text,
  InputProps,
  CommonEventFunction,
} from "@tarojs/components";
import classNames from "classnames";
import { inputRange } from "../../utils/index";

import "./index.scss";

export type HTMLFormType =
  | "tel"
  | "text"
  | "number"
  | "password"
  | "idcard"
  | "digit"
  | "safe-password"
  | "nickname";

export type FormInputProps = {
  onChange?: (value: string) => void;
  onBlur?: (value: string) => void;
  /** 当键盘输入时，触发input事件，event.detail = {value, cursor, keyCode}，处理函数可以直接 return 一个字符串，将替换输入框的内容。
   * @supported weapp, h5, rn
   */
  onInput?: CommonEventFunction<InputProps.inputEventDetail>;

  /** 输入框聚焦时触发，event.detail = { value, height }，height 为键盘高度
   * @supported weapp, h5, rn
   */
  onFocus?: CommonEventFunction<InputProps.inputForceEventDetail>;

  /** 输入框失去焦点时触发
   *
   * event.detail = {value: value}
   * @supported weapp, h5, rn
   */
  value?: string;
  maxlength?: number;
  placeholder?: string;
  type?: HTMLFormType;
  pattern?: string;
  RightComponent?: ReactNode;
  label?: string;
  labelHtml?: string;
  LeftComponent?: ReactNode;
  LeftIcon?: ReactNode;
  name?: string;
  readonly?: boolean;
  withArrow?: boolean;
  direction?: "row" | "column";
  border?: boolean;
  mainExtra?: ReactNode;
  padding?: boolean;
  focus?: boolean;
  /** 设置键盘右下角按钮的文字
   * @default done
   * @supported weapp, rn
   */
  confirmType?: "send" | "search" | "next" | "go" | "done";
  /** 用于透传 `WebComponents` 上的属性到内部 H5 标签上
   * @supported h5
   */
  nativeProps?: Record<string, unknown>;
  className?: string;
  disabled?: boolean;
  align?: "left" | "right"; // 文本对齐方式
  dataTrackId?: string;
};

export type FocusHandler = { focus: () => void };

const FormInput = forwardRef<FocusHandler, FormInputProps>((props, formRef) => {
  const {
    onChange,
    maxlength,
    value,
    type,
    placeholder,
    label,
    labelHtml,
    RightComponent,
    onBlur,
    pattern,
    LeftComponent,
    LeftIcon,
    name,
    readonly = false,
    withArrow = false,
    direction = "row",
    border = true,
    mainExtra,
    padding = false,
    className,
    disabled = false,
    align = "left",
    dataTrackId,
    ...rest
  } = props;

  const password = type === "password";

  function handleChange(e): any {
    let inputValue = inputRange(e.target.value);
    e.target.value = inputValue;
    onChange && onChange(inputValue);
  }

  function handleBlur(e): any {
    let inputValue = inputRange(e.target.value);
    onBlur && onBlur(inputValue);
  }

  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(formRef, () => ({
    focus() {
      inputRef.current?.focus();
    },
  }));

  const wrapperCls = classNames(
    "xh-form-input",
    {
      column: direction === "column",
      border,
      padding,
    },
    className
  );

  if (process.env.TARO_ENV !== "h5") {
    return (
      <View
        className={wrapperCls}
        // @ts-ignore
        dataTrackId={`${dataTrackId}-wrapper`}
      >
        <View className="xh-form-input-main">
          <View className="xh-form-input-main-left">
            {label && (
              <View className="xh-form-input-main-left-label">
                {label}
                {LeftIcon && (
                  <View className="xh-form-input-main-left-label-icon">
                    {LeftIcon}
                  </View>
                )}
              </View>
            )}
            {labelHtml && (
              <label
                dangerouslySetInnerHTML={{ __html: labelHtml }}
                className="label"
              />
            )}
            <View>{LeftComponent}</View>
          </View>
          <View className="xh-form-input-main-center">
            {readonly ? (
              <Text
                className={classNames(
                  "xh-form-input-main-center-readonly",
                  {
                    "xh-form-input-main-placeholder": !value,
                  },
                  align
                )}
              >
                {value || placeholder}
              </Text>
            ) : (
              <Input
                {...rest}
                // @ts-ignore
                dataTrackId={dataTrackId}
                password={password}
                ref={inputRef}
                className={classNames(
                  "xh-form-input-main-center-native",
                  align
                )}
                maxlength={maxlength}
                name={name}
                disabled={disabled}
                placeholder={placeholder}
                type={type as InputProps["type"]}
                value={value}
                onBlur={handleBlur}
                onInput={handleChange}
              />
            )}
          </View>
          {mainExtra && (
            <View className="xh-form-input-main-extra">{mainExtra}</View>
          )}
        </View>
        <View className="xh-form-input-right">
          {RightComponent}
          {withArrow && <View className="arrow" />}
        </View>
      </View>
    );
  }

  return (
    <div className={wrapperCls} data-track-id={`${dataTrackId}-wrapper`}>
      <div className="xh-form-input-main">
        <div className="xh-form-input-main-left">
          {label && (
            <label className="xh-form-input-main-left-label">
              {label}
              {LeftIcon && (
                <div className="xh-form-input-main-left-label-icon">
                  {LeftIcon}
                </div>
              )}
            </label>
          )}
          {labelHtml && (
            <label
              dangerouslySetInnerHTML={{ __html: labelHtml }}
              className="label"
            />
          )}
          <div>{LeftComponent}</div>
        </div>
        <div className="xh-form-input-main-center">
          {readonly ? (
            <p
              className={classNames(
                "xh-form-input-main-center-readonly",
                {
                  "xh-form-input-main-placeholder": !value,
                },
                align
              )}
            >
              {value || placeholder}
            </p>
          ) : (
            <input
              ref={inputRef}
              className={classNames("xh-form-input-main-center-native", align)}
              maxLength={maxlength}
              name={name}
              disabled={disabled}
              pattern={pattern}
              placeholder={placeholder}
              type={type}
              value={value}
              onBlur={handleBlur}
              onInput={handleChange}
              data-track-id={dataTrackId}
            />
          )}
        </div>
        {mainExtra && (
          <div className="xh-form-input-main-extra">{mainExtra}</div>
        )}
      </div>
      <div className="xh-form-input-right">
        {RightComponent}
        {withArrow && <span className="arrow" />}
      </div>
    </div>
  );
});
export default FormInput;
